import type { ActionType, ProColumns } from "@ant-design/pro-components";
import { ProTable } from "@ant-design/pro-components";
import { Drawer } from "antd";
import type { ReactNode } from "react";
import { useRef } from "react";
import type { AdminLegendGroup } from "./admin-legend-types";
import { AdminListWorkspace } from "./admin-list-workspace";
import type { AdminPageNoticeItem } from "./admin-page";
import { buildAdminProTableCommonProps } from "./admin-table";

type AdminDetailListPageProps<TData extends Record<string, unknown>> = {
  title: ReactNode;
  subTitle?: ReactNode;
  legendGroups?: AdminLegendGroup[];
  errorMessage?: string | null;
  loading?: boolean;
  rowKey: string;
  columns: ProColumns<TData>[];
  dataSource: TData[];
  persistenceKey: string;
  selectedRecord?: TData | null;
  drawerTitle?: ReactNode;
  drawerWidth?: number;
  onCloseDrawer: () => void;
  renderDrawerContent: (record: TData) => ReactNode;
};

export function AdminDetailListPage<TData extends Record<string, unknown>>({
  title,
  subTitle,
  legendGroups = [],
  errorMessage,
  loading,
  rowKey,
  columns,
  dataSource,
  persistenceKey,
  selectedRecord,
  drawerTitle,
  drawerWidth = 760,
  onCloseDrawer,
  renderDrawerContent,
}: AdminDetailListPageProps<TData>) {
  const actionRef = useRef<ActionType | null>(null);
  const notices: AdminPageNoticeItem[] = errorMessage
    ? [{ key: "error", type: "error", showIcon: true, title: errorMessage }]
    : [];

  return (
    <AdminListWorkspace
      title={title}
      subTitle={subTitle}
      legendGroups={legendGroups}
      notices={notices}
      table={
        <ProTable<TData>
          actionRef={actionRef}
          rowKey={rowKey}
          search={false}
          {...(loading === undefined ? {} : { loading })}
          columns={columns}
          dataSource={dataSource}
          toolBarRender={() => []}
          tableAlertRender={false}
          tableAlertOptionRender={false}
          {...buildAdminProTableCommonProps<TData>({ persistenceKey })}
        />
      }
    >
      <Drawer
        size={drawerWidth}
        open={Boolean(selectedRecord)}
        title={drawerTitle}
        onClose={onCloseDrawer}
      >
        {selectedRecord ? renderDrawerContent(selectedRecord) : null}
      </Drawer>
    </AdminListWorkspace>
  );
}
